import { useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface Professional {
  id: string;
  full_name: string | null;
  total_patients: number;
  total_documents: number;
  total_results: number;
}

interface BulkDeleteProfessionalsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  professionals: Professional[];
  onSuccess: () => void;
}

export function BulkDeleteProfessionalsModal({
  open,
  onOpenChange,
  professionals,
  onSuccess
}: BulkDeleteProfessionalsModalProps) {
  const [confirmation, setConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState<{
    patients: number;
    documents: number;
    lab_results: number;
    health_records: number;
    categories: number;
    subcategories: number;
    files: number;
    total_deleted: number;
  } | null>(null);

  const totalProfessionals = professionals.length;
  const totalPatients = professionals.reduce((sum, p) => sum + p.total_patients, 0);
  const totalDocuments = professionals.reduce((sum, p) => sum + p.total_documents, 0);
  const totalResults = professionals.reduce((sum, p) => sum + p.total_results, 0);

  const confirmationText = `EXCLUIR ${totalProfessionals} PROFISSIONAIS`;
  const isConfirmed = confirmation === confirmationText;

  const handleBulkDelete = async () => {
    if (!isConfirmed) return;

    try {
      setIsDeleting(true);
      setProgress(10);

      const professionalIds = professionals.map(p => p.id);

      setProgress(30);

      const { data, error } = await supabase.functions.invoke('delete-professional-cascade', {
        body: { professionalIds }
      });

      setProgress(90);

      if (error) throw error;

      setProgress(100);
      setStats(data.stats);

      toast.success('Exclusão em lote concluída', {
        description: `${data.stats.total_deleted} registros removidos permanentemente`
      });

      setTimeout(() => {
        onSuccess();
        onOpenChange(false);
        setConfirmation("");
        setStats(null);
        setProgress(0);
      }, 3000);

    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir profissionais', {
        description: error instanceof Error ? error.message : 'Erro desconhecido'
      });
      setProgress(0);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <AlertDialogTitle>Excluir Múltiplos Profissionais Permanentemente</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-4 pt-4">
            {stats ? (
              <Alert className="border-green-500 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold text-green-900">✅ Exclusão em lote concluída com sucesso!</p>
                    <div className="text-sm space-y-1 text-green-800">
                      <p>• {stats.patients} pacientes</p>
                      <p>• {stats.documents} documentos</p>
                      <p>• {stats.lab_results} resultados de exames</p>
                      <p>• {stats.health_records} registros de saúde</p>
                      <p>• {stats.categories} categorias</p>
                      <p>• {stats.subcategories} subcategorias</p>
                      <p>• {stats.files} arquivos</p>
                      <p className="font-semibold mt-2">Total: {stats.total_deleted} registros removidos</p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-semibold">⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!</p>
                    <p className="mt-2">Você está prestes a excluir permanentemente:</p>
                  </AlertDescription>
                </Alert>

                <div className="bg-muted p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-semibold">Profissionais selecionados:</p>
                      <p className="text-2xl font-bold text-destructive">{totalProfessionals}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Total de pacientes:</p>
                      <p className="text-2xl font-bold">{totalPatients}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Total de documentos:</p>
                      <p className="text-2xl font-bold">{totalDocuments}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Total de resultados:</p>
                      <p className="text-2xl font-bold">{totalResults}</p>
                    </div>
                  </div>
                </div>

                <div className="max-h-40 overflow-y-auto border rounded-lg p-3 space-y-2">
                  <p className="text-sm font-semibold mb-2">Profissionais que serão excluídos:</p>
                  {professionals.map((prof) => (
                    <div key={prof.id} className="text-sm p-2 bg-muted rounded flex justify-between items-center">
                      <span className="font-medium">{prof.full_name || 'Sem nome'}</span>
                      <span className="text-muted-foreground text-xs">
                        {prof.total_patients} pac. • {prof.total_documents} docs
                      </span>
                    </div>
                  ))}
                </div>

                <Alert>
                  <AlertDescription className="text-sm">
                    <p className="font-semibold mb-2">Serão excluídos PERMANENTEMENTE:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Todos os {totalProfessionals} profissionais selecionados</li>
                      <li>Todos os {totalPatients} pacientes vinculados</li>
                      <li>Todos os {totalDocuments} documentos</li>
                      <li>Todos os {totalResults} resultados de exames</li>
                      <li>Todos os registros de saúde</li>
                      <li>Todas as categorias e configurações</li>
                      <li>Todos os arquivos no storage</li>
                      <li>As contas de autenticação</li>
                    </ul>
                    <p className="mt-2 font-semibold text-destructive">Esta ação NÃO PODE SER DESFEITA!</p>
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="confirmation" className="font-semibold">
                    Digite "{confirmationText}" para confirmar:
                  </Label>
                  <Input
                    id="confirmation"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder={confirmationText}
                    disabled={isDeleting}
                    className="font-mono"
                  />
                </div>

                {isDeleting && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Excluindo profissionais e dados...</span>
                      <span className="font-semibold">{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                )}
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!stats && (
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={!isConfirmed || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo {totalProfessionals} profissionais...
                </>
              ) : (
                `Excluir ${totalProfessionals} Profissionais Permanentemente`
              )}
            </Button>
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
